import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { uploadCompressedImage } from '../lib/imageUpload';
import { useGroupContext } from '../lib/groups/context';
import { User, Lock, Database, HelpCircle, Shield, Bell, Zap, Cloud, Globe, RefreshCw, AlertTriangle, UserX, FileText, Users, Settings as SettingsIcon, Upload, ChevronDown, Archive, Plus, X } from 'lucide-react';
import ArchivedContacts from './ArchivedContacts';
import ImportsManagement from './ImportsManagement';
import DuplicateDetection from './DuplicateDetection';
import NotificationToast from './NotificationToast';
import ConfirmationModal from './ConfirmationModal';
import { useNotifications } from '../hooks/useNotifications';
import { loadSharePreferences, saveSharePreferences, defaultSharePreferences, SharePreferences } from '../utils/sharePrefs';
import { getPreferredTimeZone, getSupportedTimeZones, setPreferredTimeZone, getGmtOffsetLabel } from '../utils/timezone';
// import QRCode from 'qrcode'; // removed: no MFA/TOTP
// Use core groups/profile helpers instead of local API wrappers
import { deleteInviteByTokenCore, listGroupInvitesCore, inviteToGroupCore, updateGroupMemberRoleCore, removeGroupMemberCore, transferOwnershipCore, updateGroupCore, upsertUserProfileCore as upsertUserProfileCoreFromCore, getTimezoneCore, getStartViewCore, getClaimedContactIdCore, setClaimedContactIdCore, listVaultMembersWithProfilesCore, getGroupByIdCore, findGroupBySlugCore, uploadVaultLogoCore, getUserProfileCore, setMemberChosenContactCore, clearMemberChosenContactCore, softDeleteVaultCore, restoreVaultCore } from '@gv/core';
import { listContactsCore, updateContactCore, getContactCore, createContactCore } from '@gv/core';

interface SettingsProps {
    user: any;
    personalProfile?: string;
    onPersonalProfileChange?: (profile: string) => void;
    onContactSelect?: (contact: any) => void;
}

type TabType = 'profile' | 'account' | 'vault';

// Helpers to read slug and validate it
const RESERVED = new Set(['auth', 'login', 'reset-password', 'code', 'preview']);
const getSlugFromPath = () => {
    const s = (window.location.pathname.split('/').filter(Boolean)[0] || '').trim();
    return s && !RESERVED.has(s.toLowerCase()) ? s : '';
};
const validateSlug = (s: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s) && s.length <= 48 && !RESERVED.has(s);

import SquareLoader from './SquareLoader';
// MFA Manager removed — app uses email-only codes; no authenticator app flows.

export default function Settings({ user, personalProfile, onPersonalProfileChange, onContactSelect }: SettingsProps) {
    // Local inline component hoisted to avoid JSX inner declarations causing parser confusion
    const InviteRow: React.FC<{ inv: any; onCancel: (token: string) => Promise<void>; copiedToken: string; setCopiedToken: (t: string) => void; }> = ({ inv, onCancel, copiedToken, setCopiedToken }) => {
        const [deleting, setDeleting] = React.useState(false);
        const copied = copiedToken === inv.token;
        return (
            <div className="flex items-center justify-between gap-4 p-3 bg-gray-900 rounded border border-gray-700">
                <div className="flex items-center justify-between gap-2 min-w-0 flex-1">
                    <div className="text-sm text-gray-300 truncate">{inv.email}</div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-800 text-gray-200 border border-gray-700 font-mono tracking-widest hover:bg-gray-700 cursor-pointer"
                            onClick={async () => {
                                try { await navigator.clipboard.writeText(inv.token); } catch { }
                                setCopiedToken(inv.token);
                                setTimeout(() => setCopiedToken(''), 1000);
                            }}
                            title="Copy code"
                        >
                            {inv.token}
                        </button>
                        {copied && <span className="text-emerald-400 text-[10px]">Copied</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="text-xs px-2 py-1 rounded border border-red-700 text-red-300 hover:bg-red-800 disabled:opacity-50"
                        disabled={deleting}
                        onClick={async () => {
                            setDeleting(true);
                            try { await onCancel(inv.token); } finally { setDeleting(false); }
                        }}
                    >
                        {deleting ? 'Canceling…' : 'Cancel'}
                    </button>
                </div>
            </div>
        );
    };
    const { activeGroupId, groups, vaultSlug, reload } = useGroupContext();
    const { notification, showNotification, hideNotification } = useNotifications();
    const [activeTab, setActiveTab] = useState<TabType>('profile');
    const [activeSection, setActiveSection] = useState<string | null>(null);
    // Removed legacy profile editing state (first/last/email/phone/company/role/notification prefs)
    // per simplification: Profile tab now only shows My Contact + Sharing.
    const [loading] = useState(false); // retained placeholder to avoid wider refactors
    // About Me removed
    // Sharing preferences
    const [sharePrefs, setSharePrefs] = useState<SharePreferences>(() => {
        try { return loadSharePreferences(activeGroupId); } catch { return { ...defaultSharePreferences }; }
    });
    const [sharePrefsSaved, setSharePrefsSaved] = useState(false);
    const [showTemplateInfo, setShowTemplateInfo] = useState(false);
    // Account ↔ Contact linking
    const [linkedContact, setLinkedContact] = useState<{ id: string; name: string; avatar?: string | null } | null>(null);
    const [linkingOpen, setLinkingOpen] = useState(false);
    const [linkQuery, setLinkQuery] = useState('');
    const [linkSuggestions, setLinkSuggestions] = useState<Array<{ id: string; name: string; avatar?: string | null }>>([]);
    // Vault contact selection
    const [vaultContact, setVaultContact] = useState<{ id: string; name: string; avatar?: string | null } | null>(null);
    const [vaultContactOpen, setVaultContactOpen] = useState(false);
    const [vaultContactQuery, setVaultContactQuery] = useState('');
    const [vaultContactSuggestions, setVaultContactSuggestions] = useState<Array<{ id: string; name: string; avatar?: string | null }>>([]);
    // Removed hasChanges + initialData (no profile form anymore)
    // Vault logo
    const [vaultImageUrl, setVaultImageUrl] = React.useState<string | null>(null);
    const [ownerId, setOwnerId] = React.useState<string | null>(null);
    const [groupId, setGroupId] = React.useState<string | null>(null);
    const [groupName, setGroupName] = React.useState<string>('');
    const [originalGroupName, setOriginalGroupName] = React.useState<string>('');
    const [nameHistory, setNameHistory] = React.useState<string[]>([]);
    const [nameFuture, setNameFuture] = React.useState<string[]>([]);
    const [nameSaved, setNameSaved] = React.useState(false);
    const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [logoSaving, setLogoSaving] = React.useState(false);
    const [logoSaved, setLogoSaved] = React.useState(false);
    const [isOwner, setIsOwner] = React.useState<boolean>(false);
    const [plan, setPlan] = React.useState<'personal' | 'family' | 'business'>('personal');
    // Track the last committed plan from the server so we can revert selection on failure
    const committedPlanRef = React.useRef<'personal' | 'family' | 'business'>('personal');
    const [inviteRole, setInviteRole] = React.useState<'admin' | 'editor' | 'viewer'>('admin');
    const [slug, setSlug] = React.useState<string>(() => getSlugFromPath());
    const [newSlug, setNewSlug] = React.useState<string>('');
    const [slugHistory, setSlugHistory] = React.useState<string[]>([]);
    const [slugFuture, setSlugFuture] = React.useState<string[]>([]);
    const [slugSaved, setSlugSaved] = React.useState(false);
    const [slugCheck, setSlugCheck] = React.useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
    const [savingSlug, setSavingSlug] = React.useState(false);
    const [savingName, setSavingName] = React.useState(false);
    const [members, setMembers] = React.useState<Array<{ user_id: string; role: string; email?: string; avatar_url?: string; name?: string; }>>([]);
    const [groupInvites, setGroupInvites] = React.useState<Array<{ id: string; group_id: string; email: string; invited_by: string; desired_role: string | null; token: string; created_at: string; invite_code?: string | null; accepted?: boolean | null; revoked?: boolean | null; declined?: boolean | null; declined_at?: string | null }>>([]);
    const [inviteCopiedToken, setInviteCopiedToken] = React.useState<string>('');
    // Invite member UI state
    const [inviteOpen, setInviteOpen] = React.useState(false);
    const [inviteEmail, setInviteEmail] = React.useState('');
    const [inviteLoading, setInviteLoading] = React.useState(false);
    const [inviteSuggestions, setInviteSuggestions] = React.useState<Array<{ id: string; name: string; email?: string | null; avatar?: string | null }>>([]);
    // UI modal for plan downgrade block
    const [planBlockOpen, setPlanBlockOpen] = React.useState(false);
    // Time Zone state
    const [timeZone, setTimeZone] = useState<string>(() => {
        try { return getPreferredTimeZone(); } catch { return 'UTC'; }
    });
    const [tzSaving, setTzSaving] = useState(false);
    // Startup page preference
    const startViewOptions = [
        { value: 'dashboard', label: 'Home' },
        { value: 'contacts', label: 'Contacts' },
        { value: 'collections', label: 'Collections' },
        { value: 'map', label: 'Map' },
        { value: 'todo', label: 'Follow-up' },
        { value: 'calendar', label: 'Calendar' },
        // Split “Add Contacts” into two options per request
        { value: 'quick-add-contact', label: 'Add Contact' }, // Quick add flow
        { value: 'create-contact', label: 'New Contact' },    // Full create page
        { value: 'contacts-search', label: 'Search' },
        { value: 'settings', label: 'Settings' }
    ] as const;
    type StartView = typeof startViewOptions[number]['value'];
    const [startView, setStartView] = useState<StartView>('dashboard');
    const [savingStartView, setSavingStartView] = useState(false);
    const [startViewSaved, setStartViewSaved] = useState(false);
    const [tzSaved, setTzSaved] = useState(false);
    // Delete vault UI state
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteUndoInfo, setDeleteUndoInfo] = useState<{ token: string; until: string } | null>(null);
    // Login email change state
    // Secure multi-step login email change flow
    const [loginEmail, setLoginEmail] = useState<string>(user?.email || ''); // current authoritative email (read-only until flow completes)
    const [loginEmailError, setLoginEmailError] = useState<string>('');
    const [emailPhase, setEmailPhase] = useState<'idle' | 'verify-current' | 'enter-new' | 'verify-new'>('idle');
    const [currentCodeInput, setCurrentCodeInput] = useState('');
    const [generatedCurrentCode, setGeneratedCurrentCode] = useState<string>('');
    const [newEmail, setNewEmail] = useState('');
    const [newCodeInput, setNewCodeInput] = useState('');
    const [generatedNewCode, setGeneratedNewCode] = useState<string>('');
    const [emailBusy, setEmailBusy] = useState(false);
    const [copiedLogin, setCopiedLogin] = useState(false); // internal copy feedback (no explicit label shown now)

    // Small OTP input component (6 digits) reusing a single string state
    const OtpInput: React.FC<{ value: string; onChange: (v: string) => void; disabled?: boolean; autoFocus?: boolean; }> = ({ value, onChange, disabled, autoFocus }) => {
        const length = 6;
        const chars = (value || '').padEnd(length, ' ').slice(0, length).split('');
        const refs = React.useRef<HTMLInputElement[]>([]);
        const setChar = (idx: number, c: string) => {
            const clean = c.replace(/[^0-9]/g, '');
            if (!clean) return;
            const arr = chars.slice();
            arr[idx] = clean;
            const next = arr.join('').replace(/\s/g, '');
            onChange(next);
            if (idx < length - 1) {
                refs.current[idx + 1]?.focus();
                refs.current[idx + 1]?.select();
            }
        };
        return (
            <div className="flex gap-2" role="group" aria-label="One-time code">
                {chars.map((ch, i) => (
                    <input
                        key={i}
                        ref={el => { if (el) refs.current[i] = el; }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={ch.trim()}
                        autoFocus={autoFocus && i === 0}
                        disabled={disabled}
                        onChange={e => {
                            const v = e.target.value;
                            if (v === '') {
                                const arr = chars.slice();
                                arr[i] = ' ';
                                onChange(arr.join('').replace(/\s/g, ''));
                                return;
                            }
                            setChar(i, v);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Backspace' && !chars[i].trim()) {
                                if (i > 0) {
                                    refs.current[i - 1]?.focus();
                                    refs.current[i - 1]?.select();
                                }
                            } else if (e.key === 'ArrowLeft' && i > 0) {
                                refs.current[i - 1]?.focus();
                            } else if (e.key === 'ArrowRight' && i < length - 1) {
                                refs.current[i + 1]?.focus();
                            }
                        }}
                        className="w-10 h-12 text-center text-lg font-semibold tracking-widest rounded border border-gray-700 bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    />
                ))}
            </div>
        );
    };
    // Collapsible config panels
    const [showStartupConfig, setShowStartupConfig] = useState(false);
    const [showTzConfig, setShowTzConfig] = useState(false);
    // Editing buffers for dropdowns
    const [editingStartView, setEditingStartView] = useState<StartView>('dashboard');
    const [editingTimeZone, setEditingTimeZone] = useState<string>(() => {
        try { return getPreferredTimeZone(); } catch { return 'UTC'; }
    });
    // Time zone list query for filtering in the scrollable list
    const [tzListQuery, setTzListQuery] = useState('');
    // Sync group context into local state (groupId, slug, name, role flags)
    React.useEffect(() => {
        setGroupId(activeGroupId);
        const pathSlug = vaultSlug || getSlugFromPath();
        setSlug(pathSlug);
        // Show the real current Vault URL value instead of leaving it blank
        setNewSlug(pathSlug || '');
        // If we know the group, set the name from context as a default
        const match = groups.find(g => g.group.id === activeGroupId);
        if (match?.group?.name) {
            setGroupName(match.group.name);
            setOriginalGroupName(match.group.name);
            setNameHistory([]);
            setNameFuture([]);
        }
        // Load vault image if available in context or fetch from DB
        if (activeGroupId) {
            const ctxImg = (match?.group as any)?.image_url || null;
            setVaultImageUrl(ctxImg || null);
            (async () => {
                try {
                    const g = await getGroupByIdCore(supabase as any, activeGroupId);
                    if (g?.image_url) setVaultImageUrl(g.image_url);
                } catch { /* ignore */ }
            })();
        } else {
            setVaultImageUrl(null);
            setOwnerId(null);
        }
        // Derive owner status directly from context memberships
        if (match) {
            // If ownerId is known (from members fetch), use it; otherwise fallback to context role flag
            setIsOwner((match.role === 'owner') || (!!ownerId && ownerId === user?.id));
            const p = (match.group as any)?.plan as any;
            if (p === 'personal' || p === 'family' || p === 'business') {
                setPlan(p);
                committedPlanRef.current = p;
                try { localStorage.setItem(`gv:vaultPlan:${match.group.id}`, p); } catch { }
            } else {
                // Fallback to last known saved plan if group context didn't include it yet
                try {
                    const cached = localStorage.getItem(`gv:vaultPlan:${match.group.id}`) as any;
                    if (cached === 'personal' || cached === 'family' || cached === 'business') {
                        setPlan(cached);
                        committedPlanRef.current = cached as any;
                    }
                } catch { }
            }
        } else {
            setIsOwner(false);
        }
    }, [activeGroupId, vaultSlug, groups, ownerId, user?.id]);


    // Handle when duplicates are found during CSV import
    const handleDuplicatesFound = () => {
        // Duplicates will be handled automatically by the import process
        showNotification('Duplicates detected and handled automatically.', 'success');
    };

    // Handle when pending contacts are found during CSV import
    const handlePendingContactsFound = () => {
        // All contacts will be processed automatically by AI
        showNotification('All contacts processed successfully by AI.', 'success');
    };

    // Handle when contacts are updated
    const handleContactsUpdate = () => {
        // Contacts updated successfully
    };

    // Hydrate sharing prefs on mount (in case SSR vs client timing)
    useEffect(() => {
        try { setSharePrefs(loadSharePreferences(activeGroupId)); } catch { /* ignore */ }
    }, [activeGroupId]);

    // Removed vault logo local storage hydration

    React.useEffect(() => {
        (async () => {
            try {
                if (!groupId) return;
                // Load current active invites for this group
                try {
                    const invs = await listGroupInvitesCore(supabase as any, groupId);
                    setGroupInvites(invs as any);
                } catch (e) {
                    console.error('Failed to load group invites', e);
                    showNotification('Failed to load invites. Check database migrations and RLS.', 'error');
                    setGroupInvites([]);
                }
                const enriched = await listVaultMembersWithProfilesCore(supabase as any, groupId);
                setMembers(enriched as any);
                const ownerRow = (enriched || []).find((m: any) => m.role === 'owner');
                try { setOwnerId(ownerRow?.user_id || null); } catch { }
            } catch (e) {
                console.warn('Load members failed', e);
                setMembers([]);
            }
        })();
    }, [groupId]);

    // Load linked contact (if any) using core, with robust fallback when schema/RLS differs
    useEffect(() => {
        (async () => {
            try {
                let cid: string | null = null;
                try {
                    cid = await getClaimedContactIdCore(supabase as any, user.id);
                } catch (error) {
                    // On any error, try local fallback
                    const ls = localStorage.getItem(`gv:claimed_contact_id:${user.id}`);
                    if (ls) cid = ls;
                }
                if (cid) {
                    const c = await getContactCore(supabase as any, cid);
                    if (c) setLinkedContact({ id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Me', avatar: c.profile_image_url || (c as any).photo || null });
                } else {
                    // No remote cid; try local fallback too
                    const ls = localStorage.getItem(`gv:claimed_contact_id:${user.id}`);
                    if (ls) {
                        try {
                            const c = await getContactCore(supabase as any, ls);
                            if (c) setLinkedContact({ id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Me', avatar: c.profile_image_url || (c as any).photo || null });
                        } catch { }
                    }
                }
            } catch { }
        })();
    }, [user?.id]);

    // Contact chooser suggestions for linking (via core)
    useEffect(() => {
        let active = true;
        const q = linkQuery.trim();
        if (!q) { setLinkSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase as any, { query: q, limit: 8 });
                if (!active) return;
                const mapped = (rows || []).map((c: any) => ({
                    id: c.id,
                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || (c.email || c.email_1_value || 'Unknown'),
                    avatar: (c as any).profile_image_url || (c as any).photo || null,
                }));
                setLinkSuggestions(mapped);
            } catch (e) {
                if (active) setLinkSuggestions([]);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [linkQuery]);

    // Load vault contact selection for current user in current vault
    useEffect(() => {
        (async () => {
            if (!groupId || !user?.id) {
                setVaultContact(null);
                return;
            }
            try {
                // Get the vault member contacts data
                const { data: vmc } = await supabase
                    .from('vault_member_contacts')
                    .select('contact_id')
                    .eq('group_id', groupId)
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (vmc?.contact_id) {
                    const contact = await getContactCore(supabase as any, vmc.contact_id);
                    if (contact) {
                        setVaultContact({
                            id: contact.id,
                            name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact',
                            avatar: contact.profile_image_url || null
                        });
                    }
                } else {
                    setVaultContact(null);
                }
            } catch (e) {
                console.warn('Failed to load vault contact selection:', e);
                setVaultContact(null);
            }
        })();
    }, [groupId, user?.id]);

    // Vault contact chooser suggestions
    useEffect(() => {
        let active = true;
        const q = vaultContactQuery.trim();
        if (!q) { setVaultContactSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase as any, { query: q, limit: 8 });
                if (!active) return;
                const mapped = (rows || []).map((c: any) => ({
                    id: c.id,
                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || (c.email || c.email_1_value || 'Unknown'),
                    avatar: (c as any).profile_image_url || (c as any).photo || null,
                }));
                setVaultContactSuggestions(mapped);
            } catch (e) {
                if (active) setVaultContactSuggestions([]);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [vaultContactQuery]);

    const handleSignOut = async () => {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
            // Navigate home without full reload; group context will re-evaluate
            try {
                window.history.pushState({}, '', '/');
                reload();
            } catch {
                window.location.href = '/';
            }
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    // Availability check moved into saveNewSlug; keep a tiny helper for inline status if needed
    const checkSlugAvailability = async () => {
        const desired = newSlug.trim().toLowerCase();
        if (!validateSlug(desired)) { setSlugCheck('invalid'); return; }
        try {
            const existing = await findGroupBySlugCore(supabase as any, desired);
            if (!existing || existing.id === groupId) setSlugCheck('available');
            else setSlugCheck('taken');
        } catch {
            setSlugCheck('taken');
        }
    };

    const saveVaultName = async () => {
        if (!groupId) return;
        setSavingName(true);
        try {
            await updateGroupCore(supabase as any, groupId, { name: groupName });
            try { localStorage.setItem(`gv:vaultName:${slug}`, groupName); } catch { }
            setOriginalGroupName(groupName);
            setNameSaved(true);
            setTimeout(() => setNameSaved(false), 1500);
        } catch (e) {
            console.error('Saving vault name failed', e);
        } finally {
            setSavingName(false);
        }
    };

    const saveNewSlug = async () => {
        const desired = newSlug.trim().toLowerCase();
        if (!groupId) return;
        if (!validateSlug(desired)) { setSlugCheck('invalid'); return; }
        setSavingSlug(true);
        try {
            // Ensure availability in handler
            const existing = await findGroupBySlugCore(supabase as any, desired);
            if (existing && existing.id !== groupId) { setSlugCheck('taken'); return; }
            await updateGroupCore(supabase as any, groupId, { slug: desired } as any);
            try {
                const nameVal = localStorage.getItem(`gv:vaultName:${slug}`);
                if (nameVal) localStorage.setItem(`gv:vaultName:${desired}`, nameVal);
            } catch { }
            try {
                window.history.pushState({}, '', `/${desired}`);
                reload();
            } catch {
                window.location.href = `/${desired}`;
            }
            setSlug(desired);
            setSlugSaved(true);
            setTimeout(() => setSlugSaved(false), 1500);
        } catch (e) {
            console.error('Saving slug failed', e);
        } finally {
            setSavingSlug(false);
        }
    };

    // Logo upload removed

    const changeMemberRole = async (userId: string, role: 'admin' | 'member') => {
        if (!groupId) return;
        try {
            // @ts-ignore
            const { error } = await updateGroupMemberRoleCore(supabase as any, groupId, userId, role);
            if (error) throw error;
            setMembers(ms => ms.map(m => m.user_id === userId ? { ...m, role } : m));
        } catch (e) {
            console.error('set role failed', e);
            try {
                const msg = (e as any)?.message || 'Failed to change role';
                showNotification(msg, 'error');
            } catch { /* ignore */ }
        }
    };

    const transferOwnership = async (userId: string) => {
        if (!groupId) return;
        // Do not allow transferring ownership to yourself
        try {
            if (userId === user.id) {
                try { showNotification('You cannot transfer ownership to your own account.', 'error'); } catch { }
                return;
            }
        } catch { /* ignore */ }
        try {
            // @ts-ignore
            const { error } = await transferOwnershipCore(supabase as any, groupId, userId);
            if (error) throw error;
            // reflect in UI
            setMembers(ms => ms.map(m => {
                if (m.user_id === userId) return { ...m, role: 'owner' };
                if (m.role === 'owner') return { ...m, role: 'admin' };
                return m;
            }));
            setIsAdmin(true); // caller becomes admin after transfer; UI may change if you re-check role
        } catch (e) {
            console.error('ownership transfer failed', e);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'account', label: 'Account', icon: SettingsIcon },
        { id: 'vault', label: 'Vault', icon: Users }
    ];

    const go = (suffix: string) => {
        const base = slug ? `/${slug}` : '';
        window.location.href = `${base}${suffix.startsWith('/') ? '' : '/'}${suffix}`;
    };

    const copyInviteCode = async (token: string, code?: string | null) => {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            setInviteCopiedToken(token);
            setTimeout(() => setInviteCopiedToken(''), 1000);
        } catch { /* ignore */ }
    };

    const cancelInvite = async (token: string) => {
        try {
            await deleteInviteByTokenCore(supabase as any, token);
            setGroupInvites(prev => prev.filter(i => i.token !== token));
        } catch (e) {
            console.error('Cancel invite failed', e);
        }
    };

    const removeMember = async (userId: string) => {
        if (!groupId) return;
        try {
            const confirmed = window.confirm('Remove this member from the vault? They will lose access.');
            if (!confirmed) return;
            // @ts-ignore
            const { error } = await removeGroupMemberCore(supabase as any, groupId, userId);
            if (error) throw error;
            setMembers(ms => ms.filter(m => m.user_id !== userId));
            try { showNotification('Member removed', 'success'); } catch { }
        } catch (e) {
            console.error('Remove member failed', e);
            try { showNotification('Failed to remove member', 'error'); } catch { }
        }
    };

    // Removed bulk remove functionality by request — owners can remove members individually

    // Load tz and start view from profile via core if available
    useEffect(() => {
        (async () => {
            try {
                if (!user?.id) return;
                const tz = await getTimezoneCore(supabase as any, user.id);
                if (tz) {
                    setTimeZone(tz);
                    try { setPreferredTimeZone(tz); } catch { }
                }
                const sv = (await getStartViewCore(supabase as any, user.id)) as StartView | null;
                setStartView(sv as StartView);
            } catch { }
        })();
    }, [user?.id]);

    const saveTimeZone = async () => {
        setTzSaving(true);
        try {
            // Persist locally first so UI updates immediately
            setPreferredTimeZone(timeZone);
            // Save via core; ignore if schema/RLS prevents
            try {
                await upsertUserProfileCoreFromCore(supabase as any, user.id, { timezone: timeZone } as any);
            } catch { /* ignore per development policy: surface via console */ }
        } finally {
            setTzSaving(false);
        }
    };

    // Lightweight reusable card styling helper (non-component to avoid re-render complexity)
    const cardClass = "gv-card bg-[linear-gradient(145deg,#0d0d0d,#050505)] border border-gray-800/70 hover:border-gray-700 transition-colors rounded-xl p-6 shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_2px_8px_-2px_rgba(0,0,0,0.7),0_4px_24px_-4px_rgba(0,0,0,0.6)]";
    const subtleTitle = "text-[11px] font-semibold tracking-[0.15em] text-gray-400/80 mb-5";

    return (
        <div className="space-y-10">
            {notification && (
                <NotificationToast notification={notification} onClose={hideNotification} />
            )}
            {/* Tab Navigation - refined luxury segmented bar */}
            <div className="flex justify-center">
                <div className="inline-flex items-center gap-1 rounded-full border border-gray-800/70 bg-[linear-gradient(145deg,#111,#090909)] px-2 py-2 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05),0_4px_16px_-4px_rgba(0,0,0,0.7)]">
                    {tabs.map(tab => {
                        const active = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as TabType)}
                                className={`relative px-5 py-2.5 rounded-full text-sm font-medium tracking-wide flex items-center gap-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 ${active ? 'text-black bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.6)]' : 'text-gray-300 hover:text-white hover:bg-gray-800/70'}`}
                                aria-current={active ? 'page' : undefined}
                            >
                                <tab.icon className="w-4 h-4 opacity-90" />
                                <span>{tab.label}</span>
                                {active && (
                                    <span className="absolute inset-0 rounded-full ring-1 ring-black/10" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
                {/* Account Tab - Personal Profile + Sign Out */}
                {activeTab === 'account' && (
                    <div className="space-y-6">
                        {/* Startup Page Preference */}
                        <div className={cardClass}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="text-lg font-semibold text-white tracking-wide">Startup Page</h3>
                                    <div className="text-sm text-gray-400 mt-1">Current: <span className="text-gray-200 font-medium">{(startViewOptions.find(o => o.value === startView)?.label) || 'Home'}</span></div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {startViewSaved && (
                                        <span className="text-xs px-2 py-1 rounded bg-emerald-700 text-white border border-emerald-600">Saved</span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => { setEditingStartView(startView); setShowStartupConfig(v => !v); }}
                                        className="px-3 py-2 rounded border border-gray-700 text-gray-200 hover:bg-gray-800"
                                        aria-expanded={showStartupConfig}
                                    >
                                        {showStartupConfig ? 'Close' : 'Change'}
                                    </button>
                                </div>
                            </div>
                            {showStartupConfig && (
                                <div className="mt-2 p-3 border border-gray-800 rounded bg-gray-900">
                                    <label className="block text-sm text-gray-400 mb-2">Choose startup page</label>
                                    <div role="radiogroup" aria-label="Startup page" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {startViewOptions.map(opt => {
                                            const selected = editingStartView === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setEditingStartView(opt.value as StartView)}
                                                    className={`text-left w-full p-3 rounded border transition-colors ${selected ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'}`}
                                                    aria-pressed={selected}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className={`inline-block h-4 w-4 rounded-full border ${selected ? 'border-blue-400 bg-blue-500' : 'border-gray-500'}`} />
                                                        <span className="font-medium">{opt.label}</span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <button
                                            onClick={async () => {
                                                if (savingStartView) return;
                                                setSavingStartView(true);
                                                try {
                                                    await upsertUserProfileCoreFromCore(supabase as any, user.id, { start_view: editingStartView } as any);
                                                    try { localStorage.setItem('gv:startView', editingStartView); } catch { }
                                                    setStartView(editingStartView);
                                                    setStartViewSaved(true);
                                                    setTimeout(() => setStartViewSaved(false), 1200);
                                                    setShowStartupConfig(false);
                                                } catch (e) {
                                                    console.error('Failed to save startup page', e);
                                                    showNotification('Failed to save startup page', 'error');
                                                } finally {
                                                    setSavingStartView(false);
                                                }
                                            }}
                                            className="px-4 py-2 bg-white text-black hover:bg-gray-100 font-semibold rounded disabled:opacity-60"
                                            disabled={savingStartView || editingStartView === startView}
                                        >
                                            {savingStartView ? 'Saving…' : 'Save'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setEditingStartView(startView); setShowStartupConfig(false); }}
                                            className="px-3 py-2 rounded border border-gray-700 text-gray-200 hover:bg-gray-800"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Login Email Section (clean, inserted after Startup section) */}
                        <div className={`${cardClass} relative`}>
                            <h3 className="text-lg font-semibold text-white tracking-wide mb-4">Login Email</h3>
                            {loginEmailError && (
                                <div className="mb-3 px-3 py-2 rounded border border-red-800 bg-red-900/30 text-red-300 text-xs">{loginEmailError}</div>
                            )}
                            {emailPhase === 'idle' && (
                                <div className="max-w-sm">
                                    <button
                                        type="button"
                                        onClick={async () => { try { await navigator.clipboard.writeText(loginEmail); setCopiedLogin(true); setTimeout(() => setCopiedLogin(false), 800); } catch { } }}
                                        className="w-full px-4 py-3 rounded-md bg-gray-900 border border-gray-700 text-gray-200 font-mono text-sm hover:bg-gray-800 transition-colors text-left"
                                        title="Click to copy"
                                    >{loginEmail}</button>
                                    <div className="mt-3 flex justify-start">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLoginEmailError('');
                                                const code = String(Math.floor(100000 + Math.random() * 900000));
                                                setGeneratedCurrentCode(code);
                                                try { console.info('[DEV] Current email verification code:', code); } catch { }
                                                setEmailPhase('verify-current');
                                            }}
                                            className="px-5 py-2.5 bg-white text-black rounded-md font-semibold text-sm hover:bg-gray-100"
                                        >Change</button>
                                    </div>
                                </div>
                            )}
                            {emailPhase === 'verify-current' && (
                                <div className="space-y-5 max-w-sm">
                                    <div>
                                        <div className="text-sm font-medium text-white mb-1">Verify It's You</div>
                                        <div className="text-xs text-gray-400">Enter the 6‑digit code sent to <span className="text-gray-200 font-medium">{loginEmail}</span>.</div>
                                    </div>
                                    <OtpInput value={currentCodeInput} onChange={v => { setCurrentCodeInput(v); if (loginEmailError) setLoginEmailError(''); }} autoFocus />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (currentCodeInput === generatedCurrentCode) {
                                                    setEmailPhase('enter-new');
                                                    setCurrentCodeInput('');
                                                } else {
                                                    setLoginEmailError('Incorrect code');
                                                }
                                            }}
                                            className="flex-1 px-4 py-2.5 bg-white text-black rounded-md font-semibold disabled:opacity-50"
                                            disabled={currentCodeInput.length !== 6}
                                        >Continue</button>
                                        <button
                                            type="button"
                                            onClick={() => { setEmailPhase('idle'); setGeneratedCurrentCode(''); setCurrentCodeInput(''); setLoginEmailError(''); }}
                                            className="px-4 py-2.5 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm"
                                        >Cancel</button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const code = String(Math.floor(100000 + Math.random() * 900000));
                                            setGeneratedCurrentCode(code);
                                            try { console.info('[DEV] Resent current email code:', code); } catch { }
                                        }}
                                        className="text-xs text-gray-400 hover:text-gray-200"
                                    >Resend code</button>
                                </div>
                            )}
                            {emailPhase === 'enter-new' && (
                                <div className="space-y-5 max-w-sm">
                                    <div>
                                        <div className="text-sm font-medium text-white mb-1">Choose New Email</div>
                                        <div className="text-xs text-gray-400">Enter the new email you want to use.</div>
                                    </div>
                                    <input
                                        autoFocus
                                        type="email"
                                        value={newEmail}
                                        onChange={e => setNewEmail(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 rounded"
                                        placeholder="new@example.com"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!newEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail)) { setLoginEmailError('Enter a valid email'); return; }
                                                setLoginEmailError('');
                                                const code = String(Math.floor(100000 + Math.random() * 900000));
                                                setGeneratedNewCode(code);
                                                try { console.info('[DEV] New email verification code:', code); } catch { }
                                                setEmailPhase('verify-new');
                                            }}
                                            className="flex-1 px-4 py-2.5 bg-white text-black rounded-md font-semibold disabled:opacity-50"
                                            disabled={!newEmail}
                                        >Send Code</button>
                                        <button
                                            type="button"
                                            onClick={() => { setEmailPhase('idle'); setNewEmail(''); setGeneratedCurrentCode(''); setGeneratedNewCode(''); setLoginEmailError(''); }}
                                            className="px-4 py-2.5 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm"
                                        >Cancel</button>
                                    </div>
                                </div>
                            )}
                            {emailPhase === 'verify-new' && (
                                <div className="space-y-5 max-w-sm">
                                    <div>
                                        <div className="text-sm font-medium text-white mb-1">Confirm New Email</div>
                                        <div className="text-xs text-gray-400">Enter the 6‑digit code sent to <span className="text-gray-200 font-medium">{newEmail}</span>.</div>
                                    </div>
                                    <OtpInput value={newCodeInput} onChange={v => { setNewCodeInput(v); if (loginEmailError) setLoginEmailError(''); }} autoFocus />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                if (newCodeInput !== generatedNewCode) { setLoginEmailError('Incorrect code'); return; }
                                                setLoginEmailError('');
                                                setEmailBusy(true);
                                                try {
                                                    const { error } = await supabase.auth.updateUser({ email: newEmail });
                                                    if (error) { setLoginEmailError(error.message || 'Failed to update email'); }
                                                    else {
                                                        setLoginEmail(newEmail);
                                                        setNewEmail('');
                                                        setGeneratedCurrentCode('');
                                                        setGeneratedNewCode('');
                                                        setNewCodeInput('');
                                                        setEmailPhase('idle');
                                                    }
                                                } catch (e: any) {
                                                    setLoginEmailError(e?.message || 'Update failed');
                                                } finally { setEmailBusy(false); }
                                            }}
                                            className="flex-1 px-4 py-2.5 bg-white text-black rounded-md font-semibold disabled:opacity-50"
                                            disabled={newCodeInput.length !== 6 || emailBusy}
                                        >Done</button>
                                        <button
                                            type="button"
                                            onClick={() => { setEmailPhase('enter-new'); setNewCodeInput(''); setGeneratedNewCode(''); setLoginEmailError(''); }}
                                            className="px-4 py-2.5 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm"
                                            disabled={emailBusy}
                                        >Back</button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const code = String(Math.floor(100000 + Math.random() * 900000));
                                            setGeneratedNewCode(code);
                                            try { console.info('[DEV] Resent new email code:', code); } catch { }
                                        }}
                                        className="text-xs text-gray-400 hover:text-gray-200"
                                        disabled={emailBusy}
                                    >Resend code</button>
                                </div>
                            )}
                        </div>

                        {/* Time Zone Section */}
                        <div className={cardClass}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="text-lg font-semibold text-white tracking-wide">Time Zone</h3>
                                    <div className="text-sm text-gray-400 mt-1">Current: <span className="text-gray-200 font-medium">{timeZone}</span></div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {tzSaved && (
                                        <span className="text-xs px-2 py-1 rounded bg-emerald-700 text-white border border-emerald-600">Saved</span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => { setEditingTimeZone(timeZone); setShowTzConfig(v => !v); }}
                                        className="px-3 py-2 rounded border border-gray-700 text-gray-200 hover:bg-gray-800"
                                        aria-expanded={showTzConfig}
                                    >
                                        {showTzConfig ? 'Close' : 'Choose'}
                                    </button>
                                </div>
                            </div>
                            {showTzConfig && (
                                <div className="mt-2 p-3 border border-gray-800 rounded bg-gray-900">
                                    <label className="block text-sm text-gray-400 mb-1">Select time zone</label>
                                    <input
                                        type="text"
                                        value={tzListQuery}
                                        onChange={(e) => setTzListQuery(e.target.value)}
                                        placeholder="Search"
                                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 rounded mb-2"
                                    />
                                    <div className="max-h-72 overflow-auto rounded border border-gray-800">
                                        {getSupportedTimeZones()
                                            .filter(tz => tz.toLowerCase().includes(tzListQuery.trim().toLowerCase()))
                                            .map(tz => {
                                                const selected = editingTimeZone === tz;
                                                return (
                                                    <button
                                                        key={tz}
                                                        type="button"
                                                        onClick={() => setEditingTimeZone(tz)}
                                                        className={`w-full text-left px-3 py-2 border-b border-gray-800 last:border-b-0 ${selected ? 'bg-blue-500/10 text-white' : 'hover:bg-gray-800 text-gray-200'}`}
                                                        aria-pressed={selected}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className={`inline-block h-4 w-4 rounded-full border ${selected ? 'border-blue-400 bg-blue-500' : 'border-gray-500'}`} />
                                                            <span className="font-medium truncate">{tz}</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                    </div>
                                    {/* Offset removed for minimal display */}
                                    <div className="mt-3 flex items-center gap-2">
                                        <button
                                            onClick={async () => {
                                                if (tzSaving) return;
                                                setTzSaving(true);
                                                try {
                                                    setPreferredTimeZone(editingTimeZone);
                                                    setTimeZone(editingTimeZone);
                                                    try {
                                                        await upsertUserProfileCoreFromCore(supabase as any, user.id, { timezone: editingTimeZone } as any);
                                                    } catch { /* ignore RLS/column errors */ }
                                                    setTzSaved(true);
                                                    setTimeout(() => setTzSaved(false), 1200);
                                                    setShowTzConfig(false);
                                                } finally {
                                                    setTzSaving(false);
                                                }
                                            }}
                                            className="px-4 py-2 bg-white text-black hover:bg-gray-100 font-semibold rounded disabled:opacity-60"
                                            disabled={tzSaving || editingTimeZone === timeZone}
                                        >
                                            {tzSaving ? 'Saving…' : 'Save'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setEditingTimeZone(timeZone); setShowTzConfig(false); }}
                                            className="px-3 py-2 rounded border border-gray-700 text-gray-200 hover:bg-gray-800"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sign Out Section */}
                        <div className={cardClass}>
                            <h3 className="text-lg font-semibold text-white tracking-wide mb-4">Sign Out</h3>
                            <p className="text-gray-400 mb-4">
                                Sign out of your account on this device.
                            </p>
                            <button
                                onClick={handleSignOut}
                                className="px-6 py-3 bg-red-600 text-white hover:bg-red-700 transition-colors font-semibold"
                            >
                                SIGN OUT
                            </button>
                        </div>
                    </div>
                )}

                {/* Profile Tab - My Contact, Personal Profile, Sharing */}
                {activeTab === 'profile' && (
                    <div className="space-y-6">
                        {/* Me (Linked Contact) Section - simplified to read-only card with small recycle icon */}
                        <div className={cardClass}>
                            <h3 className="text-lg font-semibold text-white tracking-wide mb-3">My Contact</h3>
                            <div className="text-gray-400 text-sm mb-4">Link your account to a contact card used across the app.</div>
                            {linkedContact ? (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); try { window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: linkedContact.id } } as any)); } catch { } } }}
                                    onClick={() => {
                                        try {
                                            window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: linkedContact.id } } as any));
                                        } catch { /* no-op */ }
                                    }}
                                    className="flex items-center gap-4 p-2 -m-2 rounded hover:bg-gray-900 cursor-pointer"
                                    title="Open my contact card"
                                >
                                    <div className="w-16 h-16 rounded-md bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center">
                                        {linkedContact.avatar ? (
                                            <img src={linkedContact.avatar} alt={linkedContact.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-gray-400">👤</span>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium truncate">{linkedContact.name}</div>
                                        <div className="text-xs text-gray-400">This contact is “you.”</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setLinkingOpen(true); }}
                                        title="Relink to a different contact"
                                        aria-label="Relink My Contact"
                                        className="w-8 h-8 rounded-sm border border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800 flex items-center justify-center"
                                    >
                                        ♻️
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-md bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center">
                                        <span className="text-gray-500">👤</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium">Not linked</div>
                                        <div className="text-xs text-gray-400">Choose your contact to link</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setLinkingOpen(true)}
                                        title="Choose My Contact"
                                        aria-label="Choose My Contact"
                                        className="w-8 h-8 rounded-sm border border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800 flex items-center justify-center"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Personal Profile section removed per simplification */}

                        {/* Sharing Preferences */}
                        <div className={cardClass}>
                            <h3 className="text-lg font-semibold text-white tracking-wide mb-4">Sharing</h3>
                            <p className="text-gray-400 text-sm mb-4">Customize the single message template used when you share a contact.</p>
                            <div className="space-y-4">
                                <div className="border border-gray-800 p-4 bg-gray-900/40 relative">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-300">Message template</label>
                                        <button
                                            type="button"
                                            onClick={() => setShowTemplateInfo(v => !v)}
                                            className="p-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 text-xs"
                                            aria-label="Template help"
                                        >
                                            ?
                                        </button>
                                    </div>
                                    <textarea
                                        value={sharePrefs.template || ''}
                                        onChange={(e) => setSharePrefs(p => ({ ...p, template: e.target.value }))}
                                        rows={5}
                                        placeholder={`Example:\nHey {name}. It's {my_name}. It was great meeting you at {location}. We should hang out sometime.`}
                                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                    />
                                    {showTemplateInfo && (
                                        <div className="mt-2 text-[11px] text-gray-400 border border-gray-800 rounded p-2 bg-gray-950/80">
                                            <div className="font-semibold mb-1 text-gray-300">Placeholders</div>
                                            <div><code className="px-1 py-0.5 bg-gray-800 border border-gray-700 text-gray-200">{`{name}`}</code> = the person you just added</div>
                                            <div><code className="px-1 py-0.5 bg-gray-800 border border-gray-700 text-gray-200">{`{my_name}`}</code> = your name</div>
                                            <div><code className="px-1 py-0.5 bg-gray-800 border border-gray-700 text-gray-200">{`{location}`}</code> = where you met (if captured)</div>
                                            <div className="mt-1">Only these placeholders are supported.</div>
                                        </div>
                                    )}
                                </div>
                                {/* Default message textarea removed */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <label className="flex items-center gap-2 text-sm text-gray-300">
                                        <input type="checkbox" className="rounded" checked={sharePrefs.includeEmail} onChange={(e) => setSharePrefs(p => ({ ...p, includeEmail: e.target.checked }))} />
                                        Include email
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-300">
                                        <input type="checkbox" className="rounded" checked={sharePrefs.includePhone} onChange={(e) => setSharePrefs(p => ({ ...p, includePhone: e.target.checked }))} />
                                        Include phone/mobile
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-300">
                                        <input type="checkbox" className="rounded" checked={sharePrefs.includeCompanyTitle} onChange={(e) => setSharePrefs(p => ({ ...p, includeCompanyTitle: e.target.checked }))} />
                                        Include title and company
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-300">
                                        <input type="checkbox" className="rounded" checked={sharePrefs.includeLinkedIn} onChange={(e) => setSharePrefs(p => ({ ...p, includeLinkedIn: e.target.checked }))} />
                                        Include LinkedIn link
                                    </label>
                                </div>
                                <div className="pt-2">
                                    <button
                                        onClick={() => {
                                            saveSharePreferences(sharePrefs, activeGroupId);
                                            setSharePrefsSaved(true);
                                            setTimeout(() => setSharePrefsSaved(false), 1500);
                                            showNotification('Sharing preferences saved', 'success');
                                        }}
                                        className="px-6 py-3 font-semibold transition-colors bg-white text-black hover:bg-gray-100"
                                    >
                                        {sharePrefsSaved ? 'Saved' : 'Save Sharing Preferences'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Vault Tab: contains vault settings, archived contacts, and imports */}
                {activeTab === 'vault' && (
                    <div className="space-y-6">
                        {/* Vault Settings */}
                        <div className={cardClass}>
                            <h3 className="text-lg font-semibold text-white tracking-wide mb-6">Vault Settings</h3>
                            {!isOwner && (
                                <p className="mb-3 text-xs text-amber-400">Only vault owners can change vault name or slug.</p>
                            )}

                            <div className="space-y-6">
                                {/* Vault Plan */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Plan</label>
                                    <div className="flex items-center gap-3">
                                        {isOwner ? (
                                            <>
                                                <select
                                                    className="px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 rounded"
                                                    value={plan}
                                                    onChange={async (e) => {
                                                        const newPlan = e.target.value as 'personal' | 'family' | 'business';
                                                        const prev = committedPlanRef.current;
                                                        setPlan(newPlan);
                                                        if (!groupId) { setPlan(prev); return; }
                                                        // Block switching to Personal when other members exist; revert to previous plan
                                                        if (newPlan === 'personal') {
                                                            const nonOwners = (members || []).filter(m => m.role !== 'owner');
                                                            if (nonOwners.length > 0) {
                                                                setPlan(prev);
                                                                setPlanBlockOpen(true);
                                                                return;
                                                            }
                                                        }
                                                        try {
                                                            await updateGroupCore(supabase as any, groupId, { plan: newPlan });
                                                            committedPlanRef.current = newPlan;
                                                            try { localStorage.setItem(`gv:vaultPlan:${groupId}`, newPlan); } catch { }
                                                            showNotification('Plan updated', 'success');
                                                            try { await reload(); } catch { /* non-fatal */ }
                                                        } catch (err) {
                                                            console.error('Update plan failed', err);
                                                            showNotification('Failed to update plan', 'error');
                                                            setPlan(prev);
                                                        }
                                                    }}
                                                >
                                                    <option value="personal">Personal</option>
                                                    <option value="family">Family</option>
                                                    <option value="business">Business</option>
                                                </select>
                                                <ConfirmationModal
                                                    isOpen={planBlockOpen}
                                                    title="Cannot switch to Personal"
                                                    message="To switch to Personal, you must remove all other members from this vault first."
                                                    confirmText="Got it"
                                                    cancelText="Close"
                                                    type="warning"
                                                    onConfirm={() => setPlanBlockOpen(false)}
                                                    onCancel={() => setPlanBlockOpen(false)}
                                                />
                                            </>
                                        ) : (
                                            <div className="px-3 py-2 bg-gray-800 border border-gray-600 text-gray-300 rounded capitalize">
                                                {plan}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {isOwner
                                            ? "Personal: no invites. Family: invite with full access. Business: invite with roles (admin/editor/viewer)."
                                            : `This vault uses the ${plan} plan (set by vault owner).`
                                        }
                                    </p>
                                </div>
                                {/* Vault Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Vault Name</label>
                                    <div className="flex gap-2 items-center">
                                        {isOwner ? (
                                            <>
                                                <input
                                                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    value={groupName}
                                                    onChange={e => {
                                                        const v = e.target.value;
                                                        setNameHistory(h => [...h, groupName]);
                                                        setNameFuture([]);
                                                        setGroupName(v);
                                                    }}
                                                    onKeyDown={e => {
                                                        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                                                            e.preventDefault();
                                                            setNameHistory(h => {
                                                                if (h.length === 0) return h;
                                                                const prev = h[h.length - 1];
                                                                setNameFuture(f => [groupName, ...f]);
                                                                setGroupName(prev);
                                                                return h.slice(0, -1);
                                                            });
                                                        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
                                                            e.preventDefault();
                                                            setNameFuture(f => {
                                                                if (f.length === 0) return f;
                                                                const next = f[0];
                                                                setNameHistory(h => [...h, groupName]);
                                                                setGroupName(next);
                                                                return f.slice(1);
                                                            });
                                                        }
                                                    }}
                                                />
                                                <button
                                                    className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-semibold rounded-lg disabled:bg-gray-600 disabled:text-gray-400"
                                                    onClick={saveVaultName}
                                                    disabled={savingName || groupName === originalGroupName}
                                                >
                                                    {savingName ? 'Saving…' : (nameSaved ? 'Saved' : 'Save')}
                                                </button>
                                                {groupName !== originalGroupName && (
                                                    <span className="text-xs text-gray-400">Unsaved changes</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-full px-4 py-2 bg-gray-800 border border-gray-600 text-gray-300 rounded-lg">
                                                    {groupName}
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        const url = `${window.location.origin}/${slug || groupId}`;
                                                        navigator.clipboard.writeText(url);
                                                        showNotification('Vault URL copied to clipboard', 'success');
                                                    }}
                                                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
                                                    title="Copy vault URL"
                                                >
                                                    Copy URL
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Vault Logo */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Vault Logo</label>
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <button
                                                type="button"
                                                className={`group w-16 h-16 rounded bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center ${isOwner ? 'hover:border-gray-600' : 'opacity-60 cursor-not-allowed'}`}
                                                onClick={() => { if (isOwner) fileInputRef.current?.click(); }}
                                                aria-label={vaultImageUrl ? 'Change vault logo' : 'Choose vault logo'}
                                                disabled={!isOwner || logoSaving}
                                            >
                                                {vaultImageUrl ? (
                                                    <img src={vaultImageUrl} alt="Vault logo" className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp" alt="Default logo" className="w-8 h-8 opacity-70" />
                                                )}
                                                {/* Hover overlay */}
                                                {isOwner && !logoSaving && (
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-[11px] text-white">
                                                        Change
                                                    </div>
                                                )}
                                                {/* Saving overlay */}
                                                {logoSaving && (
                                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                        {/* Unified square loader */}
                                                        <SquareLoader size="xs" />
                                                    </div>
                                                )}
                                                {/* Saved badge */}
                                                {logoSaved && !logoSaving && (
                                                    <div className="absolute bottom-1 right-1 bg-green-700 text-white text-[10px] rounded px-1.5 py-0.5">
                                                        Saved
                                                    </div>
                                                )}
                                            </button>
                                            {vaultImageUrl && isOwner && (
                                                <button
                                                    type="button"
                                                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 border border-gray-700 text-gray-300 hover:bg-gray-800 flex items-center justify-center"
                                                    title="Remove logo"
                                                    aria-label="Remove logo"
                                                    onClick={async () => {
                                                        if (!groupId || logoSaving) return;
                                                        try {
                                                            setLogoSaving(true);
                                                            await updateGroupCore(supabase as any, groupId, { image_url: null });
                                                            setVaultImageUrl(null);
                                                            try { localStorage.setItem(`gv:logoBust:${groupId}`, String(Date.now())); } catch { }
                                                            setLogoSaved(true);
                                                            setTimeout(() => setLogoSaved(false), 1500);
                                                            try { await reload(); } catch { }
                                                        } catch (e) { /* ignore */ }
                                                        finally { setLogoSaving(false); }
                                                    }}
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={!isOwner}
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file || !groupId) return;
                                                    try {
                                                        setLogoSaving(true);
                                                        // Use core uploader to dedicated 'vault-logos' bucket
                                                        const url = await uploadVaultLogoCore(supabase as any, file);
                                                        // Persist to vaults.image_url
                                                        await updateGroupCore(supabase as any, groupId, { image_url: url });
                                                        setVaultImageUrl(url);
                                                        try { localStorage.setItem(`gv:logoBust:${groupId}`, String(Date.now())); } catch { }
                                                        setLogoSaved(true);
                                                        setTimeout(() => setLogoSaved(false), 1500);
                                                        showNotification('Vault logo updated', 'success');
                                                        try { await reload(); } catch { /* non-fatal */ }
                                                    } catch (err) {
                                                        console.error('Upload failed', err);
                                                        showNotification('Failed to upload logo', 'error');
                                                    } finally {
                                                        setLogoSaving(false);
                                                        try { if (e.target) (e.target as HTMLInputElement).value = ''; } catch { }
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            <div>Click the logo to choose a new image.</div>
                                            <div>Square images work best. Shown on invites and vault header.</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Vault Slug (Vault URL) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Vault URL</label>
                                    <div className="flex gap-2 items-center">
                                        <input
                                            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            value={newSlug}
                                            onChange={e => {
                                                const v = e.target.value.toLowerCase();
                                                setSlugHistory(h => [...h, newSlug]);
                                                setSlugFuture([]);
                                                setNewSlug(v);
                                                setSlugCheck('idle');
                                            }}
                                            onKeyDown={e => {
                                                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                                                    e.preventDefault();
                                                    setSlugHistory(h => {
                                                        if (h.length === 0) return h;
                                                        const prev = h[h.length - 1];
                                                        setSlugFuture(f => [newSlug, ...f]);
                                                        setNewSlug(prev);
                                                        return h.slice(0, -1);
                                                    });
                                                } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
                                                    e.preventDefault();
                                                    setSlugFuture(f => {
                                                        if (f.length === 0) return f;
                                                        const next = f[0];
                                                        setSlugHistory(h => [...h, newSlug]);
                                                        setNewSlug(next);
                                                        return f.slice(1);
                                                    });
                                                }
                                            }}
                                            disabled={!isOwner}
                                        />
                                        <button
                                            className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-semibold rounded-lg disabled:bg-gray-600 disabled:text-gray-400"
                                            onClick={saveNewSlug}
                                            disabled={!isOwner || savingSlug || !newSlug || newSlug === slug}
                                        >
                                            {savingSlug ? 'Saving…' : (slugSaved ? 'Saved' : 'Save')}
                                        </button>
                                        {newSlug && newSlug !== slug && (
                                            <span className="text-xs text-gray-400">Unsaved changes</span>
                                        )}
                                    </div>
                                    {slugCheck === 'available' && <p className="text-xs text-green-400 mt-1">✓ Available</p>}
                                    {slugCheck === 'taken' && <p className="text-xs text-red-400 mt-1">✗ Taken</p>}
                                    {slugCheck === 'invalid' && <p className="text-xs text-red-400 mt-1">✗ Invalid format</p>}
                                </div>
                            </div>

                            {/* Danger Zone: Delete Vault */}
                            {isOwner && (
                                <div className={`mt-6 ${cardClass}`}>
                                    <h3 className="text-lg font-semibold text-red-400 tracking-wide mb-2">Danger Zone</h3>
                                    <div className="text-sm text-gray-400 mb-3">Delete this vault. All members will lose access immediately. You can undo within 10 days.</div>
                                    {deleteUndoInfo ? (
                                        <div className="border border-emerald-700 bg-emerald-900/20 text-emerald-200 rounded p-3 mb-3 text-sm">
                                            Vault scheduled for deletion. Undo allowed until {new Date(deleteUndoInfo.until).toLocaleString()}. Keep this code safe:
                                            <div className="mt-2 p-2 rounded bg-gray-900 border border-gray-700 font-mono text-xs break-all">{deleteUndoInfo.token}</div>
                                            <div className="mt-3 flex gap-2">
                                                <button
                                                    className="px-3 py-2 rounded bg-white text-black text-sm font-semibold"
                                                    onClick={async () => {
                                                        if (!deleteUndoInfo?.token) return;
                                                        try {
                                                            const ok = await restoreVaultCore(supabase as any, deleteUndoInfo.token);
                                                            if (ok) {
                                                                setDeleteUndoInfo(null);
                                                                showNotification('Vault restored', 'success');
                                                                await reload();
                                                            } else {
                                                                showNotification('Unable to restore (expired or invalid)', 'error');
                                                            }
                                                        } catch (e) {
                                                            console.error('Restore failed', e);
                                                            showNotification('Restore failed', 'error');
                                                        }
                                                    }}
                                                >Undo Delete</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <label className="block text-xs text-gray-400 mb-1">Type "delete" to confirm</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    value={deleteConfirmText}
                                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                                    className="px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 rounded"
                                                    placeholder="delete"
                                                />
                                                <button
                                                    className="px-4 py-2 rounded bg-red-700 hover:bg-red-600 text-white font-semibold disabled:opacity-60"
                                                    disabled={deleteConfirmText.trim().toLowerCase() !== 'delete' || !groupId || deleteBusy}
                                                    onClick={async () => {
                                                        if (!groupId || deleteConfirmText.trim().toLowerCase() !== 'delete') return;
                                                        setDeleteBusy(true);
                                                        try {
                                                            const { user } = (await supabase.auth.getUser()).data;
                                                            if (!user) throw new Error('Not signed in');
                                                            const { undoToken, undoUntil } = await softDeleteVaultCore(supabase as any, groupId, user.id, 10);
                                                            setDeleteUndoInfo({ token: undoToken, until: undoUntil });
                                                            setDeleteConfirmText('');
                                                            showNotification('Vault deleted (soft). You can undo within 10 days.', 'success');
                                                            // Redirect to home and clear slug
                                                            try { window.history.pushState({}, '', '/'); } catch { window.location.href = '/'; }
                                                            await reload();
                                                        } catch (e: any) {
                                                            console.error('Delete vault failed', e);
                                                            showNotification(e?.message || 'Failed to delete vault', 'error');
                                                        } finally {
                                                            setDeleteBusy(false);
                                                        }
                                                    }}
                                                >{deleteBusy ? 'Deleting…' : 'Delete Vault'}</button>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-2">This action removes member access immediately. Data is retained for 10 days for undo.</div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Vault Members */}
                        {groupId && (
                            <div className={cardClass}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-base font-semibold text-white tracking-wide">Vault Members</h3>
                                    {isOwner && (
                                        <button
                                            onClick={() => {
                                                if (plan === 'personal') {
                                                    showNotification('Invites are only available on Family or Business plans. Your vault is Personal.', 'error');
                                                    return;
                                                }
                                                setInviteOpen(true);
                                            }}
                                            className="inline-flex items-center gap-2 px-3 py-2 text-white rounded"
                                            style={{ backgroundColor: '#2563eb' }}
                                        >
                                            <Plus className="w-4 h-4" /> Invite
                                        </button>
                                    )}
                                </div>
                                {/* Banner removed per request: always show member rows using chosen/claimed contact names */}

                                {/* Warn if no owner membership is present */}
                                {(() => {
                                    try {
                                        const hasOwner = (members || []).some(m => m.role === 'owner');
                                        if (!hasOwner) {
                                            return (
                                                <div className="mb-2 text-xs text-amber-300 bg-amber-900/20 border border-amber-800/40 rounded px-3 py-2">
                                                    No owner membership found for this vault. Roles may be inconsistent. Ensure exactly one member has role “owner”.
                                                </div>
                                            );
                                        }
                                    } catch { /* ignore */ }
                                    return null;
                                })()}

                                <div className="space-y-4">
                                    {members.map(m => {
                                        // Display label: prefer selected/claimed contact name (m.name),
                                        // else fall back to the email local-part (before @), else a generic placeholder.
                                        const label = m.name || (m.email ? (m.email.split?.('@')[0] || m.email) : 'Member');
                                        return (
                                            <div key={m.user_id} className="flex items-center justify-between gap-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 overflow-hidden rounded-full bg-gray-700">
                                                        {m.avatar_url ? (
                                                            <img src={m.avatar_url} className="h-10 w-10 object-cover" alt="Avatar" />
                                                        ) : (
                                                            <div className="flex h-10 w-10 items-center justify-center text-gray-400">👤</div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-white">
                                                            {label}
                                                        </div>
                                                        <div className="text-xs text-gray-400">
                                                            {(m.role === 'owner') ? 'Owner' : (plan === 'business' ? (m.role === 'admin' ? 'Admin' : 'Member') : 'Member')}
                                                        </div>
                                                    </div>
                                                </div>
                                                {isOwner && (
                                                    <div className="flex items-center gap-2">
                                                        {/* For the current owner row, do not render role-change or transfer controls */}
                                                        {m.role !== 'owner' ? (
                                                            <>
                                                                {/* Only Business plan exposes role toggles */}
                                                                {plan === 'business' && (
                                                                    <>
                                                                        <button
                                                                            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
                                                                            onClick={() => changeMemberRole(m.user_id, 'member')}
                                                                            disabled={m.role === 'member'}
                                                                        >
                                                                            Member
                                                                        </button>
                                                                        <button
                                                                            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
                                                                            onClick={() => changeMemberRole(m.user_id, 'admin')}
                                                                            disabled={m.role === 'admin'}
                                                                        >
                                                                            Admin
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {/* Remove member (all plans, owner only) */}
                                                                <button
                                                                    className="px-3 py-1 text-xs border border-red-700 text-red-300 rounded hover:bg-red-900/20"
                                                                    onClick={() => removeMember(m.user_id)}
                                                                >
                                                                    Remove
                                                                </button>
                                                                {/* You cannot transfer ownership to yourself */}
                                                                {m.user_id !== user.id && (
                                                                    <button
                                                                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50"
                                                                        onClick={() => transferOwnership(m.user_id)}
                                                                    >
                                                                        Transfer Ownership
                                                                    </button>
                                                                )}
                                                            </>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {/* Invites (active + declined) */}
                                    {(() => {
                                        try {
                                            if (!groupId) return null;
                                            const invites = groupInvites || [];
                                            const active = invites; // show all current invites; simplified model has no statuses

                                            return (
                                                <div className="space-y-3">
                                                    {active.length > 0 && (
                                                        <div className="space-y-2">
                                                            <div className="text-xs text-gray-400">Active invites</div>
                                                            {active.map(inv => (
                                                                <InviteRow
                                                                    key={inv.id}
                                                                    inv={inv}
                                                                    onCancel={async (token: string) => {
                                                                        await cancelInvite(token);
                                                                        setGroupInvites((prev: any[]) => prev.filter(i => i.token !== token));
                                                                        try { showNotification('Invite canceled', 'success'); } catch { }
                                                                    }}
                                                                    copiedToken={inviteCopiedToken}
                                                                    setCopiedToken={setInviteCopiedToken}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        } catch { return null; }
                                    })()}
                                    {/* Local pending removed: database is the source of truth */}
                                    {members.length === 0 && (() => {
                                        try {
                                            if (!groupId) return (<div className="text-sm text-gray-400 text-center py-8">No members yet.</div>);
                                            const key = `gv:pendingInvites:${groupId}`;
                                            const raw = localStorage.getItem(key);
                                            const list: string[] = raw ? JSON.parse(raw) : [];
                                            if (list.length) return null;
                                        } catch { }
                                        return (<div className="text-sm text-gray-400 text-center py-8">No members yet.</div>);
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Invite Member Modal */}
                        {inviteOpen && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center">
                                <div className="absolute inset-0 bg-black/60" onClick={() => setInviteOpen(false)} />
                                <div className="relative w-full sm:max-w-lg bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-white font-semibold">Invite Member</h4>
                                        <button onClick={() => setInviteOpen(false)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Close</button>
                                    </div>
                                    {plan === 'business' && (
                                        <div className="mb-3">
                                            <label className="block text-sm text-gray-400 mb-1">Role</label>
                                            <select
                                                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 rounded"
                                                value={inviteRole}
                                                onChange={(e) => setInviteRole(e.target.value as any)}
                                            >
                                                <option value="admin">Admin</option>
                                                <option value="editor">Editor</option>
                                                <option value="viewer">Viewer</option>
                                            </select>
                                        </div>
                                    )}
                                    <label className="block text-sm text-gray-400 mb-1">Email or name</label>
                                    <input
                                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 rounded"
                                        value={inviteEmail}
                                        onChange={async (e) => {
                                            const v = e.target.value;
                                            setInviteEmail(v);
                                            // Suggest contacts by email/name
                                            const q = v.trim();
                                            if (!q) { setInviteSuggestions([]); return; }
                                            try {
                                                const rows = await listContactsCore(supabase as any, { query: q, limit: 6 });
                                                const mapped = (rows || []).map((c: any) => ({
                                                    id: c.id,
                                                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                                                    email: (c as any).email || (c as any).email_1_value,
                                                    avatar: (c as any).profile_image_url || (c as any).photo || null,
                                                }));
                                                setInviteSuggestions(mapped);
                                            } catch { setInviteSuggestions([]); }
                                        }}
                                        placeholder="person@example.com"
                                        autoFocus
                                    />
                                    {inviteSuggestions.length > 0 && (
                                        <div className="mt-2 max-h-52 overflow-auto border border-gray-700 rounded-lg">
                                            {inviteSuggestions.map(s => (
                                                <button
                                                    key={s.id}
                                                    type="button"
                                                    onClick={() => setInviteEmail(s.email || s.name)}
                                                    className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-800 last:border-b-0 flex items-center gap-3"
                                                >
                                                    <div className="w-8 h-8 rounded-md bg-gray-700 overflow-hidden flex-shrink-0">
                                                        {s.avatar ? (
                                                            <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full bg-gray-700" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-medium truncate">{s.name || s.email}</div>
                                                        {s.email && <div className="text-xs text-gray-400 truncate">{s.email}</div>}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-4 flex justify-end gap-2">
                                        <button onClick={() => setInviteOpen(false)} className="px-3 py-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-700">Cancel</button>
                                        <button
                                            onClick={async () => {
                                                if (!groupId) return;
                                                const email = inviteEmail.trim();
                                                if (!email) return;
                                                setInviteLoading(true);
                                                try {
                                                    // Ensure contact exists or create one via core
                                                    let contactId: string | null = null;
                                                    try {
                                                        const rows = await listContactsCore(supabase as any, { query: email, limit: 1 });
                                                        if (rows && rows.length) contactId = rows[0].id;
                                                    } catch { }

                                                    if (!contactId) {
                                                        const [first, ...rest] = email.includes('@') ? email.split('@')[0].split(/[._\-\s]+/) : email.split(' ');
                                                        const last = rest.join(' ');
                                                        const created = await createContactCore(supabase as any, { first_name: first || email, last_name: (last || '').trim(), email } as any);
                                                        contactId = created.id;
                                                    }

                                                    // Create invite and show link + code
                                                    const inv = await inviteToGroupCore(supabase as any, groupId, email, (plan === 'business' && inviteRole === 'admin') ? 'admin' : undefined);
                                                    const code = inv.token;
                                                    const base = window.location.origin + (slug ? `/${slug}` : '');
                                                    const inviteUrl = `${base}?invite=${encodeURIComponent(inv.token)}`;
                                                    const shareText = `Invite link: ${inviteUrl}\nInvite code: ${code}`;
                                                    try {
                                                        await navigator.clipboard.writeText(shareText);
                                                        showNotification('Invite link and code copied', 'success');
                                                    } catch {
                                                        alert(shareText);
                                                    }
                                                    // Reflect immediately in Active invites list (DB is source of truth)
                                                    try { setGroupInvites(prev => [inv as any, ...(prev || [])]); } catch { }

                                                    setInviteOpen(false);
                                                    setInviteEmail('');
                                                    setInviteSuggestions([]);
                                                } catch (e) {
                                                    console.error('Invite failed', e);
                                                    try { showNotification('Failed to send invite', 'error'); } catch { }
                                                } finally {
                                                    setInviteLoading(false);
                                                }
                                            }}
                                            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
                                            disabled={inviteLoading || !inviteEmail.trim()}
                                        >
                                            {inviteLoading ? 'Sending…' : 'Send Invite'}
                                        </button>
                                    </div>
                                    <div className="mt-3 text-xs text-gray-400">
                                        After sending, you’ll get an invite link and a 6-digit code. Share both with the invitee. The email notification will not include a code.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Your Representative Contact - Uses My Contact */}
                        {groupId && (
                            <div className={cardClass}>
                                <h3 className="text-lg font-semibold text-white tracking-wide mb-4">Your Representative Contact</h3>
                                <p className="text-sm text-gray-400 mb-4">
                                    Your representative contact in this vault is automatically set to your "My Contact" from the Profile tab. Other vault members will see you as this contact.
                                </p>

                                <div className="space-y-4">
                                    {linkedContact ? (
                                        <div className="flex items-center gap-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                                            <div className="w-12 h-12 rounded-md bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center">
                                                {linkedContact.avatar ? (
                                                    <img src={linkedContact.avatar} alt={linkedContact.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-gray-500">👤</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-white font-medium">{linkedContact.name}</div>
                                                <div className="text-xs text-gray-400">Your representative contact (from My Contact)</div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('profile')}
                                                className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                                            >
                                                Change in Profile
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                                            <div className="w-12 h-12 rounded-md bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center">
                                                <span className="text-gray-500">👤</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-white font-medium">No contact selected</div>
                                                <div className="text-xs text-gray-400">Set your "My Contact" in the Profile tab to choose your representative contact</div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('profile')}
                                                className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                                            >
                                                Go to Profile
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Archived Contacts */}
                        <div className={`${cardClass} p-0 overflow-hidden`}>
                            <button
                                onClick={() => setActiveSection(activeSection === 'archived-contacts' ? null : 'archived-contacts')}
                                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-900 transition-colors text-left"
                            >
                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                    <Archive className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-lg font-medium text-white">Archived Contacts</h3>
                                        <p className="text-sm text-gray-400 break-words">View and restore archived contacts</p>
                                    </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${activeSection === 'archived-contacts' ? 'rotate-180' : ''}`} />
                            </button>
                            {activeSection === 'archived-contacts' && (
                                <div className="border-t border-gray-800">
                                    <ArchivedContacts
                                        onContactSelect={onContactSelect || (() => { })}
                                        user={user}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Imports */}
                        <div className={`${cardClass} p-0 overflow-hidden`}>
                            <button
                                onClick={() => setActiveSection(activeSection === 'imports' ? null : 'imports')}
                                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-900 transition-colors text-left"
                            >
                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                    <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-lg font-medium text-white">Import Contacts</h3>
                                        <p className="text-sm text-gray-400 break-words">Upload CSV or spreadsheets; AI will parse and organize</p>
                                    </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${activeSection === 'imports' ? 'rotate-180' : ''}`} />
                            </button>
                            {activeSection === 'imports' && (
                                <div className="border-t border-gray-800">
                                    <ImportsManagement
                                        user={user}
                                        onContactsUpdate={handleContactsUpdate}
                                        onDuplicatesFound={handleDuplicatesFound}
                                        onPendingContactsFound={handlePendingContactsFound}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Duplicate Detection */}
                        <div className={`${cardClass} p-0 overflow-hidden`}>
                            <button
                                onClick={() => setActiveSection(activeSection === 'duplicates' ? null : 'duplicates')}
                                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-900 transition-colors text-left"
                            >
                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                    <Users className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-lg font-medium text-white">Duplicate Detection</h3>
                                        <p className="text-sm text-gray-400 break-words">Find and remove or merge duplicate contacts</p>
                                    </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${activeSection === 'duplicates' ? 'rotate-180' : ''}`} />
                            </button>
                            {activeSection === 'duplicates' && (
                                <div className="border-t border-gray-800 p-4">
                                    <DuplicateDetection user={user} personalProfile={personalProfile} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {linkingOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setLinkingOpen(false)} />
                    <div className="relative w-full max-w-lg bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-semibold">Choose Your Contact</h4>
                            <button onClick={() => setLinkingOpen(false)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Close</button>
                        </div>
                        <input
                            autoFocus
                            value={linkQuery}
                            onChange={(e) => setLinkQuery(e.target.value)}
                            placeholder="Search by name or email"
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded"
                        />
                        <div className="mt-3 max-h-80 overflow-auto divide-y divide-gray-800 border border-gray-800 rounded">
                            {linkSuggestions.length === 0 ? (
                                <div className="p-3 text-gray-500 text-sm">Start typing to search your contacts…</div>
                            ) : (
                                linkSuggestions.map(s => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await setClaimedContactIdCore(supabase as any, user.id, s.id);
                                                try { localStorage.setItem(`gv:claimed_contact_id:${user.id}`, s.id); } catch { }
                                                setLinkedContact({ id: s.id, name: s.name, avatar: s.avatar });
                                                setLinkingOpen(false);
                                                setLinkQuery('');
                                                setLinkSuggestions([]);
                                            } catch (err) {
                                                console.error('Failed to link contact', err);
                                                try { showNotification('Failed to link contact', 'error'); } catch { }
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-800 flex items-center gap-3"
                                    >
                                        <div className="w-8 h-8 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                                            {s.avatar ? (
                                                <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-gray-700" />
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{s.name}</div>
                                            <div className="text-xs text-gray-400 truncate">{s.id}</div>
                                        </div>
                                    </button>
                                ))
                            )
                            }
                        </div>
                    </div>
                </div>
            )}

            {/* Vault Contact Selection Modal */}
            {vaultContactOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setVaultContactOpen(false)} />
                    <div className="relative w-full sm:max-w-lg bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-semibold">Choose Your Representative Contact</h4>
                            <button onClick={() => setVaultContactOpen(false)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Close</button>
                        </div>
                        <p className="text-sm text-gray-400 mb-4">
                            Select a contact from your vault to represent you. Other members will see this contact's name next to your membership.
                        </p>
                        <input
                            autoFocus
                            value={vaultContactQuery}
                            onChange={(e) => setVaultContactQuery(e.target.value)}
                            placeholder="Search by name or email"
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded"
                        />
                        <div className="mt-3 max-h-80 overflow-auto divide-y divide-gray-800 border border-gray-800 rounded">
                            {vaultContactSuggestions.length === 0 ? (
                                <div className="p-3 text-gray-500 text-sm">Start typing to search your contacts…</div>
                            ) : (
                                vaultContactSuggestions.map(s => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await setMemberChosenContactCore(supabase as any, groupId!, user.id, s.id);
                                                setVaultContact({ id: s.id, name: s.name, avatar: s.avatar });
                                                setVaultContactOpen(false);
                                                setVaultContactQuery('');
                                                setVaultContactSuggestions([]);
                                                // Refresh members to show updated display names
                                                const enriched = await listVaultMembersWithProfilesCore(supabase as any, groupId!);
                                                setMembers(enriched as any);
                                                showNotification('Representative contact updated', 'success');
                                            } catch (err: any) {
                                                console.error('Failed to set vault contact', err);
                                                if (err.message?.includes('already chosen')) {
                                                    showNotification('This contact is already chosen by another member in this vault', 'error');
                                                } else {
                                                    showNotification('Failed to set representative contact', 'error');
                                                }
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-800 flex items-center gap-3"
                                    >
                                        <div className="w-8 h-8 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                                            {s.avatar ? (
                                                <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-gray-700" />
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{s.name}</div>
                                            <div className="text-xs text-gray-400 truncate">{s.id}</div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
